/**
 * Familles métier des notifications — la clé du REGROUPEMENT demandé par le CCE (2026-09-16) dans le
 * panneau « الإشعارات » et le filtre de la page « كل الإشعارات ».
 *
 * Le code de famille vient du SERVEUR (déduit du type, `NotificationFamille` côté Java) ; ce fichier ne
 * porte que l'ordre d'affichage, le libellé et l'icône. Les libellés reprennent des intitulés déjà
 * présents dans les menus ou au glossaire (« أعمال المجلس », « توزيع مراقبي الدولة », « طلب المساندة »,
 * « فضاء الوثائق والمعارف », « التكوين ») : rien d'inventé, et une famille inconnue tombe dans « أخرى ».
 */
export type NotificationFamilleCode =
  | 'SEANCES'
  | 'RAPPORTS'
  | 'MISSIONS'
  | 'AFFECTATION'
  | 'DOCUMENTS'
  | 'PASSATIONS'
  | 'APPUI'
  | 'ALERTES'
  | 'FORMATION'
  | 'CAC'
  | 'QUALITE'
  | 'AUTRE';

export interface NotificationFamilleDef {
  readonly code: NotificationFamilleCode;
  readonly ar: string;
  readonly fr: string;
  /** Nom d'icône lucide. */
  readonly icon: string;
}

/** Ordre d'affichage des groupes : ce qui engage une décision d'abord, le reste ensuite. */
export const NOTIFICATION_FAMILLES: readonly NotificationFamilleDef[] = [
  { code: 'SEANCES', ar: 'أشغال المجلس', fr: 'Travaux du Conseil', icon: 'users' },
  { code: 'RAPPORTS', ar: 'التقارير', fr: 'Rapports', icon: 'file-text' },
  { code: 'MISSIONS', ar: 'مهمات الرقابة', fr: 'Missions de contrôle', icon: 'briefcase' },
  { code: 'AFFECTATION', ar: 'توزيع مراقبي الدولة', fr: 'Affectation des contrôleurs', icon: 'user-check' },
  { code: 'DOCUMENTS', ar: 'فضاء الوثائق والمعارف', fr: 'Espace documentaire', icon: 'book-open' },
  // Étapes d'une passation (2026-10-02) — le terme du glossaire, celui du menu « متابعة نقل المهام ».
  { code: 'PASSATIONS', ar: 'نقل المهام', fr: 'Passations', icon: 'arrow-left-right' },
  { code: 'APPUI', ar: 'طلبات المساندة', fr: "Demandes d'appui", icon: 'life-buoy' },
  // Gestes du Président sur une مذكرة إعلام, avisés au contrôleur (2026-10-02) — l'intitulé de l'onglet du hub.
  { code: 'ALERTES', ar: 'مذكرات الإعلام', fr: "Notes d'alerte", icon: 'bell-ring' },
  { code: 'FORMATION', ar: 'التكوين', fr: 'Formation', icon: 'graduation-cap' },
  // Récapitulatif du secrétariat CAEEP et avis à notifier (2026-09-29) — l'intitulé de la rubrique I du
  // menu de l'espace, remise par le Président.
  { code: 'CAC', ar: 'التدقيق في ملفات تعيين مراقبي الحسابات', fr: 'Nomination des commissaires aux comptes', icon: 'file-check' },
  // Revue qualité confiée à un réviseur (2026-10-12) — l'intitulé de l'espace « مراقبة جودة العمل الرقابي ».
  { code: 'QUALITE', ar: 'مراقبة جودة العمل الرقابي', fr: 'Contrôle de la qualité du travail des contrôleurs', icon: 'list-checks' },
  { code: 'AUTRE', ar: 'أخرى', fr: 'Autres', icon: 'bell' },
];

const PAR_CODE: ReadonlyMap<string, NotificationFamilleDef> = new Map(
  NOTIFICATION_FAMILLES.map((f) => [f.code, f]),
);

/** La définition d'une famille ; « أخرى » pour un code inconnu ou absent. */
export function familleDe(code: string | null | undefined): NotificationFamilleDef {
  return (code && PAR_CODE.get(code)) || NOTIFICATION_FAMILLES[NOTIFICATION_FAMILLES.length - 1];
}

/** Libellé d'une famille dans la langue courante. */
export function libelleFamille(code: string | null | undefined, lang: string): string {
  const f = familleDe(code);
  return lang === 'ar' ? f.ar : f.fr;
}
